<template>
  <page-list
    :list="list"
    :columns="changedColumns"
    :export-data-options="exportDataOptions"
    :singleActions="singleActions"
    :groupActions="groupActions"
    hiddenPin>
    <template v-slot:group-actions-prepend>
      <div class="d-flex align-items-center">
        <date-time
          class="ml-2"
          :getParams.sync="dateParam"
          :dateMode.sync="dateMode"
          paramTimeFormatter="YYYY-MM-DDTZ"
          showStartMonthTip
          @refresh="fetchData" />
      </div>
    </template>
    <template v-slot:group-actions-append>
      <div class="d-flex align-items-center">
        <a-checkbox class="ml-2" v-model="showBillAccounts">{{$t('bill.show_bill_accounts')}}</a-checkbox>
      </div>
    </template>
    <template v-slot:table-prepend>
      <total-price :currencySign="currencySign" :total="total_amount" :secondary_total="total_secondary_amount" setSecondaryToAmount />
    </template>
  </page-list>
</template>

<script>
import * as R from 'ramda'
import TotalPrice from '@Bill/sections/TotalPrice'
import currencyRequest from '@Bill/mixins/currencyRequest'
import ContractMixin from '@Bill/mixins/contract'
import { getAmountColumns } from '@Bill/utils'
import TotalPriceMixin from '@Bill/mixins/totalPrice'
import { getBrandDistinctFilter, getDistinctFieldFilter, getDomainFilter } from '@/utils/common/tableFilter'
import WindowsMixin from '@/mixins/windows'
import ListMixin from '@/mixins/list'
import ColumnsMixin from '../mixins/columns'
import SingleActionsMixin from '../mixins/singleActions'

export default {
  name: 'BillAccountsList',
  components: {
    TotalPrice,
  },
  mixins: [WindowsMixin, currencyRequest, ListMixin, ColumnsMixin, TotalPriceMixin, SingleActionsMixin, ContractMixin],
  props: {
    id: String,
    getParams: {
      type: Object,
      default: () => ({ account_id: 'accountList', details: true }),
    },
    billShared: Boolean,
    projectShared: Boolean,
  },
  data () {
    return {
      dateMode: 'month',
      dateParam: {},
      showBillAccounts: true,
      list: this.$list.createList(this, {
        id: this.id,
        resource: 'meter_cloudaccounts',
        apiVersion: 'v1',
        getParams: this.getParam,
        fetchDataCb: this.getTotal,
        noPreLoad: true,
        filterOptions: {
          id: {
            label: this.$t('table.title.id'),
          },
          name: {
            label: this.$t('table.title.name'),
            filter: true,
            formatter: val => {
              return `name.contains('${val}')`
            },
          },
          brand: getBrandDistinctFilter(),
          project_domain: getDomainFilter('project_domain', 'tenant', 'field'),
          currency: getDistinctFieldFilter({ label: this.$t('bill.currency_bill'), field: 'currency' }),
        },
      }),
      groupActions: [
        {
          label: this.$t('bill.rerun_bill'),
          permission: 'billtasks_create',
          action: () => {
            this.createDialog('BilltasksCreateDialog', {
              type: 'rerun_bill',
              data: this.list.selectedItems,
              account_id: this.list.selectedItems.map(item => item.id).join(','),
            })
          },
          meta: () => {
            const ret = { validate: this.list.selected.length > 0 }
            if (this.list.selectedItems.length) {
              this.list.selectedItems.map(obj => {
                const ownerDomain = this.isAdminMode || obj.domain_id === this.userInfo.projectDomainId
                if (!ownerDomain) {
                  ret.validate = false
                }
                if (obj.status === 'deleted') {
                  ret.validate = false
                  ret.tooltip = this.$t('bill.deleted_not_rerun')
                }
                if (!obj.tenant_id) {
                  ret.validate = false
                  ret.tooltip = this.$t('bill.please_set_project_mapping')
                }
              })
            }
            return ret
          },
        },
      ],
    }
  },
  computed: {
    changedColumns () {
      const columns = R.clone(this.columns)
      const amountColumns = getAmountColumns({
        disable_cost_conversion: this.currencyParams.disable_cost_conversion,
        dateMode: this.dateMode,
        amountContractColumn: this.billContractColumns[0] || null,
      })
      amountColumns.map(item => {
        item.sortable = false
      })
      return [...columns, ...amountColumns]
    },
    exportDataOptions () {
      return {
        title: this.$t('bill.text_244'),
        downloadType: 'local',
        items: this.changedColumns,
        genSelectedIdParams: (params, selectedItems) => {
          params.account_id = selectedItems.map(item => item.id)
          return params
        },
      }
    },
  },
  watch: {
    billShared () {
      this.fetchData()
    },
    projectShared () {
      this.fetchData()
    },
    showBillAccounts () {
      this.fetchData()
    },
  },
  created () {
    this.fetchData()
  },
  methods: {
    getParam () {
      const ret = {
        query_type: 'account_expense',
        start_date: this.$moment().startOf('month').format('YYYY-MM-DD') + 'TZ',
        end_date: this.$moment().endOf('month').format('YYYY-MM-DD') + 'TZ',
        ...this.currencyParams,
        ...this.dateParam,
        ...(this.billContractParams || {}),
      }
      if (this.billShared) {
        ret.is_prepaid_amortizing = true
      } else {
        ret.is_prepaid_amortizing = false
      }
      if (this.projectShared) {
        ret.is_project_sharing = true
      } else {
        ret.is_project_sharing = false
      }
      if (!this.showBillAccounts) {
        ret.all = true
      }
      return ret
    },
    fetchData () {
      this.list.fetchData()
    },
    handleOpenSidepage (row, tab) {
      this.sidePageTriggerHandle(this, 'BillAccountSidePage', {
        id: row.id,
        resource: row.id === 'yunion' ? () => {
          return {
            data: row,
          }
        } : 'meter_cloudaccounts',
        data: row,
        getParams: { ...this.getParams, account_id: row.id },
        dateParam: this.dateParam,
        dateMode: this.dateMode,
      }, {
        list: this.list,
        tab,
      })
    },
  },
}
</script>
